import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { AuthService } from '../../services/auth.service';

@Component({
  selector: 'app-activate-account',
  standalone: false,
  template: `
    <div class="activate-account-container">
      <h2>Activate Your Account</h2>
    
      <!-- Loading State -->
      @if (loading) {
        <div>Loading activation details...</div>
      }
    
      <!-- Error -->
      @if (!loading && error) {
        <div class="error">
          {{ errorMessage }}
        </div>
      }
    
      <!-- Activation Form -->
      @if (!loading && !success && !error) {
        <form
          [formGroup]="activationForm"
          (ngSubmit)="onSubmit()"
          >
          <input type="hidden" formControlName="action" />
          <input type="hidden" formControlName="email" />
          <input type="hidden" formControlName="oneTimeCode" />
          <!-- Password only if required -->
          @if (requiresPassword) {
            <div class="form-group">
              <label for="password">Set Your Password</label>
              <input
                id="password"
                type="password"
                formControlName="password"
                placeholder="Enter your password (8 characters mininum)"
                class="form-control"
                />
              @if (password?.invalid && (password?.dirty || password?.touched)) {
                <div
                  class="validation-error"
                  >
                  @if (password?.errors?.['required']) {
                    <small
                      >Password is required.</small
                      >
                    }
                    @if (password?.errors?.['minlength']) {
                      <small
                        >Password must be at least 8 characters.</small
                        >
                      }
                    </div>
                  }
                </div>
              }
              <button
                type="submit"
                class="primary-btn"
          [disabled]="
            submitting ||
            (requiresPassword && activationForm.get('password')?.invalid)
          "
                >
                {{ submitting ? 'Activating...' : 'Activate Account' }}
              </button>
            </form>
          }
    
          <!-- Success State -->
          @if (!loading && success) {
            <div class="success">
              Your account has been activated!
              <button class="primary-btn" (click)="navigateToLogin()">Log in</button>
            </div>
          }
        </div>
    `,
  styles: [
    `
      .activate-account-container {
        max-width: 400px;
        margin: 2rem auto;
        padding: 1rem;
        border: 1px solid #ccc;
        border-radius: 8px;
        text-align: center;
      }
      .form-group {
        margin-bottom: 1rem;
        text-align: left;
      }
      .form-control {
        width: 100%;
        padding: 0.5rem;
        margin-top: 0.25rem;
        box-sizing: border-box;
      }
      .validation-error {
        color: red;
      }
      .success {
        color: green;
        margin-top: 1rem;
      }
      .error {
        color: red;
        margin-top: 1rem;
      }
    `,
  ],
})
export class ActivateAccountComponent implements OnInit {
  activationForm: FormGroup;
  loading = true;
  submitting = false;
  success = false;
  error = false;
  errorMessage = '';
  requiresPassword = false;

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private authService: AuthService,
    private router: Router
  ) {
    this.activationForm = this.fb.group({
      action: [''],
      email: ['', [Validators.required, Validators.email]],
      oneTimeCode: ['', Validators.required],
      password: ['', [Validators.required, Validators.minLength(8)]],
    });
  }

  ngOnInit(): void {
    this.route.queryParams.subscribe((params) => {
      const action = params['action'];
      const email = params['email'];
      const oneTimeCode = params['oneTimeCode'];

      if (
        (action === 'validNewUserWithPass' || action === 'validNewUser') &&
        email &&
        oneTimeCode
      ) {
        this.requiresPassword = action === 'validNewUserWithPass';
        this.activationForm.patchValue({ action, email, oneTimeCode });
        if (!this.requiresPassword) {
          // remove validators for password when not required
          this.activationForm.get('password')?.clearValidators();
          this.activationForm.get('password')?.updateValueAndValidity();
        }
        this.loading = false;
      } else {
        this.loading = false;
        this.error = true;
        this.errorMessage = 'Invalid activation link.';
      }
    });
  }

  get password() {
    return this.activationForm.get('password');
  }

  onSubmit(): void {
    if (this.activationForm.invalid) return;

    this.submitting = true;
    const { action, email, oneTimeCode, password } = this.activationForm.value;

    const activation$ = this.requiresPassword
      ? this.authService.activateNewUserWithPassword(
          action,
          email,
          oneTimeCode,
          password
        )
      : this.authService.activateNewUser(action, email, oneTimeCode);

    activation$.subscribe({
      next: () => {
        this.submitting = false;
        this.success = true;
      },
      error: (err: any) => {
        this.submitting = false;
        this.error = true;
        this.errorMessage =
          err.error?.message || err.message || 'Activation failed';
      },
    });
  }

  navigateToLogin(): void {
    this.router.navigate(['/login']);
  }
}
